/* ═══════════════════════════════════════════════════════════════
   Permissions Registry — Granular permission keys, categories,
   and helpers for the role-based access control system.

   Pattern: `resource.action` (e.g. `specs.publish`, `tasks.edit_any`)

   Single source of truth. The backend derives its canonical list
   from here (ec2-api imports this module). Frontend gates all use
   hasPermission() against these keys. Custom roles in the admin
   center are composed from this exact set.

   Design principles:
   - Every user-visible action should have a key here — "damn near
     every action gated" was the explicit product direction.
   - Distinguish `.view_own / .view_team / .view_all` wherever
     visibility matters (orgs rarely want everyone seeing everything).
   - Distinguish `.edit_own / .edit_any` wherever "edit my own record"
     is a different capability from "edit anyone's record".
   - Review-workflow actions (`submit_for_review / approve / reject`)
     get their own keys so publisher roles can exist without edit.
   ═══════════════════════════════════════════════════════════════ */

// ─── Permission Category (for grouped UI display) ───

export interface PermissionDef {
  key: string;
  label: string;
  description?: string;
}

export interface PermissionCategory {
  id: string;
  label: string;
  description: string;
  permissions: PermissionDef[];
}

// ─── Full Permission Registry ───

export const PERMISSION_CATEGORIES: PermissionCategory[] = [
  // ──────────────────────────────────────────────────────────────
  // ADMIN CENTER — firm-level configuration
  // ──────────────────────────────────────────────────────────────
  {
    id: 'admin_center',
    label: 'Admin Center',
    description: 'Access to firm-level settings and configuration',
    permissions: [
      {
        key: 'admin_center.view',
        label: 'View Admin Center',
        description: 'Access the Admin Center page',
      },
      {
        key: 'admin_center.manage_roles',
        label: 'Manage Roles & Privileges',
        description: 'Create, edit, and delete privilege roles',
      },
      {
        key: 'admin_center.manage_billing',
        label: 'Manage Billing Setup',
        description:
          'Billing roles, fee structures, billing codes, phases, overhead categories, expense types',
      },
      {
        key: 'admin_center.manage_subscription',
        label: 'Manage Subscription',
        description: 'View and manage firm subscription tier and enabled modules',
      },
      {
        key: 'admin_center.manage_firm_settings',
        label: 'Manage Firm Settings',
        description: 'Firm name, logo, default working hours, and other firm-wide preferences',
      },
      { key: 'admin_center.manage_project_types', label: 'Manage Project Types' },
      { key: 'admin_center.manage_phases', label: 'Manage Default Phases' },
      { key: 'admin_center.manage_activity_codes', label: 'Manage Activity Codes' },
      { key: 'admin_center.manage_holidays', label: 'Manage Firm Holidays' },
      {
        key: 'admin_center.manage_departments',
        label: 'Manage Departments',
        description: 'Department lookup (MEPT, BIM, etc) used by the team page and forecast grid',
      },
      { key: 'admin_center.manage_vacation_categories', label: 'Manage Vacation Categories' },
      {
        key: 'admin_center.manage_company_roles',
        label: 'Manage Company Roles',
        description: 'Job titles / company-side role labels used for directory display',
      },
      {
        key: 'admin_center.manage_billing_roles',
        label: 'Manage Billing Roles',
        description: 'Timesheet role categories and default rates',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // TEAM MEMBERS
  // ──────────────────────────────────────────────────────────────
  {
    id: 'team_members',
    label: 'Team Members',
    description: 'Manage people in your firm',
    permissions: [
      { key: 'team_members.view', label: 'View Team' },
      {
        key: 'team_members.view_details',
        label: 'View Sensitive Details',
        description: 'See compensation, bill rates, and other restricted fields',
      },
      { key: 'team_members.create', label: 'Add Members' },
      {
        key: 'team_members.invite',
        label: 'Send Invitations',
        description: 'Invite new users via email',
      },
      { key: 'team_members.edit', label: 'Edit Members' },
      {
        key: 'team_members.edit_own',
        label: 'Edit Own Profile',
        description: 'Everyone needs this to update their own name/avatar/contact info',
      },
      {
        key: 'team_members.manage_roles',
        label: 'Assign / Change Roles',
        description: 'Change which privilege role a user is assigned to',
      },
      { key: 'team_members.deactivate', label: 'Deactivate Members' },
      { key: 'team_members.reactivate', label: 'Reactivate Members' },
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // TEAMS (org chart)
  // ──────────────────────────────────────────────────────────────
  {
    id: 'teams',
    label: 'Teams / Org Chart',
    description: 'Team groupings and reporting structure',
    permissions: [
      { key: 'teams.view', label: 'View Teams' },
      { key: 'teams.create', label: 'Create Teams' },
      {
        key: 'teams.edit',
        label: 'Edit Teams',
        description: 'Rename, assign leads, change reporting structure',
      },
      { key: 'teams.manage_members', label: 'Add / Remove Team Members' },
      { key: 'teams.delete', label: 'Delete Teams' },
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // CLIENTS / ORGANIZATIONS
  // ──────────────────────────────────────────────────────────────
  {
    id: 'organizations',
    label: 'Clients / Organizations',
    description: 'Manage client and organization records',
    permissions: [
      { key: 'organizations.view', label: 'View Clients' },
      { key: 'organizations.create', label: 'Create Clients' },
      { key: 'organizations.edit', label: 'Edit Clients' },
      {
        key: 'organizations.edit_contacts',
        label: 'Edit Client Contacts',
        description: 'Manage contacts attached to a client',
      },
      {
        key: 'organizations.archive',
        label: 'Archive Clients',
        description: 'Soft-hide clients without deleting their data',
      },
      { key: 'organizations.restore', label: 'Restore Archived Clients' },
      { key: 'organizations.delete', label: 'Delete Clients' },
      { key: 'organizations.export', label: 'Export Client List' },
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // PROJECTS
  // ──────────────────────────────────────────────────────────────
  {
    id: 'projects',
    label: 'Projects',
    description: 'Project access and management',
    permissions: [
      {
        key: 'projects.view_own',
        label: 'View Assigned Projects',
        description: 'See only projects you are assigned to',
      },
      {
        key: 'projects.view_all',
        label: 'View All Projects',
        description: 'See every project in the firm',
      },
      {
        key: 'projects.view_financials',
        label: 'View Project Financials',
        description: 'See budget, hours burned, fees, and $ invoiced on projects they can view',
      },
      {
        key: 'projects.view_all_financials',
        label: 'View All Project Financials',
        description: 'Firm-wide dollar figures across every project',
      },
      { key: 'projects.create', label: 'Create Projects' },
      { key: 'projects.edit', label: 'Edit Projects' },
      {
        key: 'projects.edit_financials',
        label: 'Edit Project Financials',
        description: 'Change budgets, fee splits, contract amounts',
      },
      {
        key: 'projects.edit_schedule',
        label: 'Edit Project Schedule',
        description: 'Change start/end dates, milestones',
      },
      {
        key: 'projects.edit_scope',
        label: 'Edit Project Scope',
        description: 'Change deliverables and scope items',
      },
      { key: 'projects.manage_phases', label: 'Manage Project Phases' },
      { key: 'projects.manage_deliverables', label: 'Manage Deliverables' },
      {
        key: 'projects.archive',
        label: 'Archive Projects',
        description: 'Mark a project inactive without deleting its data',
      },
      { key: 'projects.restore', label: 'Restore Archived Projects' },
      { key: 'projects.delete', label: 'Delete Projects' },
      {
        key: 'projects.assign_team',
        label: 'Assign Team to Projects',
        description: 'Add/remove people below you on the org chart',
      },
      {
        key: 'projects.assign_anyone',
        label: 'Assign Anyone to Projects',
        description: 'Add/remove any firm member to projects',
      },
      { key: 'projects.export', label: 'Export Project Data' },
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // SPECIFICATIONS (master specs)
  // ──────────────────────────────────────────────────────────────
  {
    id: 'specs',
    label: 'Spec Masters',
    description: 'Firm-wide master specification library',
    permissions: [
      { key: 'specs.view', label: 'View Specs' },
      {
        key: 'specs.view_history',
        label: 'View Version History',
        description: 'See past published versions of a spec',
      },
      { key: 'specs.create', label: 'Create Specs' },
      {
        key: 'specs.edit_own',
        label: 'Edit Own Drafts',
        description: 'Edit drafts you checked out yourself',
      },
      {
        key: 'specs.edit_any',
        label: 'Edit Any Draft',
        description: 'Edit drafts checked out by anyone',
      },
      {
        key: 'specs.submit_for_review',
        label: 'Submit for Review',
        description: 'Send your draft to a publisher for approval',
      },
      {
        key: 'specs.publish',
        label: 'Publish Specs',
        description: 'Approve drafts and move them to published state',
      },
      {
        key: 'specs.approve_review',
        label: 'Approve Review Requests',
        description: 'Approve drafts that others submitted for review',
      },
      { key: 'specs.reject_review', label: 'Reject Review Requests' },
      { key: 'specs.archive', label: 'Archive Specs' },
      { key: 'specs.restore', label: 'Restore Archived Specs' },
      { key: 'specs.delete', label: 'Delete Specs' },
      {
        key: 'specs.override_draft',
        label: "Override Others' Drafts",
        description: 'Force-unlock a spec another user is editing',
      },
      {
        key: 'specs.force_unlock',
        label: 'Force Unlock & Discard',
        description: "Unlock AND discard another user's draft in one action",
      },
      {
        key: 'specs.version_rollback',
        label: 'Rollback to Past Version',
        description: 'Revert a spec to an earlier published version',
      },
      { key: 'specs.variant_create', label: 'Create Variants' },
      { key: 'specs.variant_edit', label: 'Edit Variants' },
      { key: 'specs.variant_delete', label: 'Delete Variants' },
      { key: 'specs.comment', label: 'Comment on Blocks' },
      { key: 'specs.resolve_comment', label: 'Resolve Block Comments' },
      {
        key: 'specs.import',
        label: 'Import from DOCX',
        description: 'Use the harvester to import specs from Word documents',
      },
      { key: 'specs.export', label: 'Export Specs' },
    ],
  },

  // LIBRARY MODULES — feature retired in v2.12.0. The transclusion model
  // ("edit one library module, all specs auto-update") was unused in
  // production and the editor's drag/coordinate model was fundamentally
  // broken because of it (expanded-vs-raw index mismatch). All references
  // were inlined by migration 019. Permission keys here are removed; any
  // role row still carrying them in its permissions[] array is silently
  // ignored.

  // ──────────────────────────────────────────────────────────────
  // SMART BLOCKS (dynamic, context-aware spec blocks)
  // ──────────────────────────────────────────────────────────────
  // The successor to library modules. A SmartBlock is a *declaration* of
  // content to compute — author drops one into a master spec, the resolver
  // expands it against project context at compile time (Related Sections,
  // Project Identity, Issue Date, etc.). Mirrors specs.* lifecycle: any
  // firm member can draft, publishers approve before the row goes live.
  {
    id: 'smart_blocks',
    label: 'Smart Blocks',
    description:
      'Firm-wide library of dynamic, context-aware spec content (Related Sections, Project Identity, etc.)',
    permissions: [
      { key: 'smart_blocks.view', label: 'View Smart Blocks' },
      {
        key: 'smart_blocks.view_history',
        label: 'View Version History',
        description: 'See past published versions of a smart block',
      },
      { key: 'smart_blocks.create', label: 'Create Smart Blocks' },
      {
        key: 'smart_blocks.edit_own',
        label: 'Edit Own Drafts',
        description: 'Edit smart-block drafts you checked out yourself',
      },
      {
        key: 'smart_blocks.edit_any',
        label: 'Edit Any Draft',
        description: 'Edit smart-block drafts checked out by anyone',
      },
      {
        key: 'smart_blocks.submit_for_review',
        label: 'Submit for Review',
        description: 'Send your smart-block draft to a publisher for approval',
      },
      {
        key: 'smart_blocks.publish',
        label: 'Publish Smart Blocks',
        description: 'Approve drafts and move them to published state',
      },
      {
        key: 'smart_blocks.approve_review',
        label: 'Approve Review Requests',
      },
      { key: 'smart_blocks.reject_review', label: 'Reject Review Requests' },
      { key: 'smart_blocks.archive', label: 'Archive Smart Blocks' },
      { key: 'smart_blocks.restore', label: 'Restore Archived Smart Blocks' },
      { key: 'smart_blocks.delete', label: 'Delete Smart Blocks' },
      {
        key: 'smart_blocks.override_draft',
        label: "Override Others' Drafts",
      },
      {
        key: 'smart_blocks.force_unlock',
        label: 'Force Unlock & Discard',
      },
      {
        key: 'smart_blocks.version_rollback',
        label: 'Rollback to Past Version',
      },
      {
        key: 'smart_blocks.insert',
        label: 'Insert into Specs',
        description: 'Place a smart-block instance into a master spec from the picker',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // PROJECT SPECS (per-project spec forks)
  // ──────────────────────────────────────────────────────────────
  {
    id: 'project_specs',
    label: 'Project Specs',
    description: 'Per-project specification forks and issuances',
    permissions: [
      { key: 'project_specs.view', label: 'View Project Specs' },
      {
        key: 'project_specs.compile',
        label: 'Compile Masters into Projects',
        description: 'Fork master specs into a project',
      },
      { key: 'project_specs.edit_own', label: 'Edit Own Drafts' },
      { key: 'project_specs.edit_any', label: 'Edit Any Draft' },
      { key: 'project_specs.submit_for_review', label: 'Submit for Review' },
      {
        key: 'project_specs.publish',
        label: 'Publish Project Specs',
        description: 'Approve project-spec drafts',
      },
      { key: 'project_specs.approve_review', label: 'Approve Review Requests' },
      { key: 'project_specs.reject_review', label: 'Reject Review Requests' },
      {
        key: 'project_specs.create_issuance',
        label: 'Create Issuances',
        description: 'Issue IFC / Addendum / ASI sets against a project',
      },
      { key: 'project_specs.archive', label: 'Archive Project Specs' },
      { key: 'project_specs.delete', label: 'Delete Project Specs' },
      { key: 'project_specs.override_draft', label: "Override Others' Drafts" },
      { key: 'project_specs.export', label: 'Export Project Specs' },
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // FORMAS (formatting templates)
  // ──────────────────────────────────────────────────────────────
  {
    id: 'formas',
    label: 'Formas (Templates)',
    description: 'Word formatting templates and token configurations',
    permissions: [
      { key: 'formas.view', label: 'View Formas' },
      { key: 'formas.create', label: 'Create Formas' },
      { key: 'formas.edit', label: 'Edit Formas' },
      { key: 'formas.duplicate', label: 'Duplicate Formas' },
      {
        key: 'formas.set_default',
        label: 'Set Default Forma',
        description: 'Choose the firm-wide default formatting template',
      },
      { key: 'formas.archive', label: 'Archive Formas' },
      { key: 'formas.delete', label: 'Delete Formas' },
      { key: 'formas.import_template', label: 'Import .dotx Templates' },
      { key: 'formas.export_template', label: 'Export .dotx Templates' },
      {
        key: 'formas.apply_to_spec',
        label: 'Apply Forma to Spec',
        description: 'Choose which Forma a spec exports with',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // COMPILE (bulk spec compilation)
  // ──────────────────────────────────────────────────────────────
  {
    id: 'compile',
    label: 'Compile Specs',
    description: 'Bulk spec compilation and project generation',
    permissions: [
      { key: 'compile.view', label: 'View Compile Page' },
      {
        key: 'compile.run',
        label: 'Run Compile',
        description: 'Compile selected specs into a project',
      },
      { key: 'compile.bulk', label: 'Bulk Compile', description: 'Compile many specs at once' },
      { key: 'compile.export', label: 'Export Compiled Output' },
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // FORMATTER (document formatter)
  // ──────────────────────────────────────────────────────────────
  {
    id: 'formatter',
    label: 'Document Formatter',
    description: 'Standalone Word document reformatting',
    permissions: [
      { key: 'formatter.view', label: 'View Formatter' },
      {
        key: 'formatter.run',
        label: 'Format Documents',
        description: 'Reformat Word documents with a Forma',
      },
      {
        key: 'formatter.batch',
        label: 'Batch Format',
        description: 'Format multiple documents in one run',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // TIMESHEETS
  // ──────────────────────────────────────────────────────────────
  {
    id: 'timesheets',
    label: 'Timesheets',
    description: 'Time entry, submission, and approval',
    permissions: [
      { key: 'timesheets.view_own', label: 'View Own Timesheets' },
      {
        key: 'timesheets.view_team',
        label: 'View Team Timesheets',
        description: 'See timesheets for people below you on the org chart',
      },
      { key: 'timesheets.view_all', label: 'View All Timesheets' },
      { key: 'timesheets.create', label: 'Enter Time' },
      {
        key: 'timesheets.edit_own',
        label: 'Edit Own Entries',
        description: 'Edit your own time entries before submitting',
      },
      { key: 'timesheets.edit_any', label: "Edit Others' Entries" },
      { key: 'timesheets.submit', label: 'Submit Timesheets' },
      { key: 'timesheets.approve', label: 'Approve Timesheets' },
      { key: 'timesheets.reject', label: 'Reject Timesheets' },
      {
        key: 'timesheets.unlock',
        label: 'Unlock Submitted / Approved Timesheets',
        description: 'Re-open a locked timesheet so it can be edited',
      },
      { key: 'timesheets.export', label: 'Export Timesheets' },
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // VACATION / TIME OFF
  // ──────────────────────────────────────────────────────────────
  {
    id: 'vacation',
    label: 'Vacation / Time Off',
    description: 'Vacation requests and approval',
    permissions: [
      { key: 'vacation.view_own', label: 'View Own Requests' },
      { key: 'vacation.view_team', label: 'View Team Requests' },
      { key: 'vacation.view_all', label: 'View All Requests' },
      { key: 'vacation.create', label: 'Submit Requests' },
      {
        key: 'vacation.edit',
        label: 'Edit Requests',
        description: 'Modify a pending request before it is decided',
      },
      { key: 'vacation.approve', label: 'Approve / Deny Requests' },
      { key: 'vacation.cancel', label: 'Cancel Requests' },
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // INVOICES
  // ──────────────────────────────────────────────────────────────
  {
    id: 'invoices',
    label: 'Invoices',
    description: 'Invoice creation, sending, and AR tracking (no payment processing)',
    permissions: [
      {
        key: 'invoices.view_own',
        label: 'View Project Invoices',
        description: 'Invoices for projects you are on',
      },
      {
        key: 'invoices.view_all',
        label: 'View All Invoices',
        description: 'Firm-wide invoices, including projects you are not on',
      },
      {
        key: 'invoices.view_financials',
        label: 'View Invoice Amounts',
        description: 'See $ totals — some roles see invoice existence but not amounts',
      },
      { key: 'invoices.create', label: 'Create Invoices' },
      {
        key: 'invoices.generate_from_project',
        label: 'Generate From Project',
        description: 'Auto-pull billable hours/expenses into a draft invoice',
      },
      { key: 'invoices.edit', label: 'Edit Invoices' },
      { key: 'invoices.send', label: 'Send Invoices', description: 'Mark sent / generate PDF' },
      {
        key: 'invoices.mark_paid',
        label: 'Record Payments',
        description: 'Mark partial or full payment received',
      },
      { key: 'invoices.void', label: 'Void Invoices' },
      {
        key: 'invoices.archive',
        label: 'Archive Invoices',
        description: 'Hide paid or cancelled invoices from active lists',
      },
      { key: 'invoices.delete', label: 'Delete Invoices' },
      { key: 'invoices.export_pdf', label: 'Export PDF' },
      { key: 'invoices.export_csv', label: 'Export CSV' },
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // REPORTS
  // ──────────────────────────────────────────────────────────────
  {
    id: 'reports',
    label: 'Reports & Analytics',
    description: 'Run, view, and export firm reports',
    permissions: [
      { key: 'reports.view', label: 'Open Reports Page' },
      {
        key: 'reports.view_financials',
        label: 'View Financial Figures',
        description: 'Revenue, margin, burn, AR, and any $ values inside reports',
      },
      { key: 'reports.run_utilization', label: 'Run Utilization Report' },
      {
        key: 'reports.run_wip',
        label: 'Run WIP Report',
        description: 'Work-in-progress: billable hours not yet invoiced',
      },
      {
        key: 'reports.run_profitability',
        label: 'Run Project Profitability Report',
        description: 'Actual cost vs budget per project',
      },
      { key: 'reports.run_ar_aging', label: 'Run AR Aging Report' },
      { key: 'reports.run_time_by_project', label: 'Run Time By Project Report' },
      { key: 'reports.run_time_by_person', label: 'Run Time By Person Report' },
      { key: 'reports.run_forecast', label: 'Run Forecast Outlook Report' },
      { key: 'reports.export_csv', label: 'Export Report As CSV' },
      { key: 'reports.export_pdf', label: 'Export Report As PDF' },
      { key: 'reports.schedule', label: 'Schedule Recurring Reports' },
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // DASHBOARDS — saved views and per-user dashboard customization
  // ──────────────────────────────────────────────────────────────
  {
    id: 'dashboards',
    label: 'Dashboards & Saved Views',
    description: 'Personal dashboards, saved filter/grouping arrangements per user',
    permissions: [
      // The view_own/create/edit_own/delete keys exist so the standard backend
      // table-permission check can treat dashboard_views like any other
      // self-scoped table. Every regular user gets these via BASE_PERMISSIONS.
      { key: 'dashboards.view_own', label: 'View Own Saved Views' },
      {
        key: 'dashboards.view_all',
        label: 'View All Saved Views',
        description: 'Includes others private personal views — admin tier',
      },
      { key: 'dashboards.create', label: 'Create Saved Views' },
      { key: 'dashboards.edit_own', label: 'Edit Own Saved Views' },
      { key: 'dashboards.edit_any', label: "Edit Any User's Saved View" },
      { key: 'dashboards.delete', label: 'Delete Saved Views' },
      {
        key: 'dashboards.customize',
        label: 'Customize Personal Dashboard',
        description: 'Reorder/show/hide widgets on dashboards and project pages',
      },
      {
        key: 'dashboards.share_views',
        label: 'Publish Shared Views',
        description: 'Make a saved view visible to other users in the firm',
      },
      {
        key: 'dashboards.manage_firm_default',
        label: 'Manage Firm Default View',
        description: 'Set the default view a new firm member sees on each surface',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // FORECASTING
  // ──────────────────────────────────────────────────────────────
  {
    id: 'forecasting',
    label: 'Forecasting',
    description: 'Hour forecasting and resource planning grid',
    permissions: [
      { key: 'forecasting.view_own', label: 'View Own Forecasts' },
      {
        key: 'forecasting.view_team',
        label: 'View Team Forecasts',
        description: 'Team leads + PMs: see forecasts for people on the projects/teams they lead',
      },
      {
        key: 'forecasting.view_all',
        label: 'View All Forecasts',
        description: 'Firm-wide grid (admins, principals, billing roles)',
      },
      { key: 'forecasting.edit_own', label: 'Edit Own Forecasts' },
      {
        key: 'forecasting.edit_team',
        label: 'Edit Team Forecasts',
        description:
          'Team leads + PMs: assign hours to people on their projects. Server enforces project-membership.',
      },
      { key: 'forecasting.edit_any', label: 'Edit Any Forecast' },
      { key: 'forecasting.delete_team', label: 'Delete Team Forecasts' },
      { key: 'forecasting.delete_any', label: 'Delete Any Forecast' },
      {
        key: 'forecasting.bulk_assign',
        label: 'Bulk-Assign Forecasts',
        description: 'Drag-paint cell ranges, copy-week, paste-week affordances',
      },
      {
        key: 'forecasting.share_views',
        label: 'Share Forecast Views',
        description: 'Save filter/grouping arrangements visible to other users',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // TASKS
  // ──────────────────────────────────────────────────────────────
  {
    id: 'tasks',
    label: 'Tasks',
    description: 'Task management and assignment',
    permissions: [
      { key: 'tasks.view_own', label: 'View Own Tasks' },
      {
        key: 'tasks.view_team',
        label: 'View Team Tasks',
        description: 'See tasks for people below you on the org chart',
      },
      { key: 'tasks.view_all', label: 'View All Tasks', description: 'See every task in the firm' },
      { key: 'tasks.create', label: 'Create Tasks' },
      { key: 'tasks.edit_own', label: 'Edit Own Tasks' },
      { key: 'tasks.edit_any', label: 'Edit Any Task' },
      {
        key: 'tasks.archive',
        label: 'Archive Tasks',
        description: 'Mark tasks done without deleting them',
      },
      { key: 'tasks.delete', label: 'Delete Tasks' },
      {
        key: 'tasks.assign_team',
        label: 'Assign Team Tasks',
        description: 'Assign tasks to people below you on the org chart',
      },
      {
        key: 'tasks.assign_anyone',
        label: 'Assign Anyone Tasks',
        description: 'Assign tasks to any firm member',
      },
      { key: 'tasks.complete', label: 'Complete Tasks' },
      { key: 'tasks.comment', label: 'Comment on Tasks' },
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // EXPENSES
  // ──────────────────────────────────────────────────────────────
  {
    id: 'expenses',
    label: 'Expenses',
    description: 'Expense tracking and approval',
    permissions: [
      { key: 'expenses.view_own', label: 'View Own Expenses' },
      { key: 'expenses.view_team', label: 'View Team Expenses' },
      { key: 'expenses.view_all', label: 'View All Expenses' },
      { key: 'expenses.create', label: 'Submit Expenses' },
      { key: 'expenses.edit_own', label: 'Edit Own Expenses' },
      { key: 'expenses.edit_any', label: "Edit Others' Expenses" },
      { key: 'expenses.approve', label: 'Approve Expenses' },
      { key: 'expenses.reject', label: 'Reject Expenses' },
      { key: 'expenses.delete', label: 'Delete Expenses' },
      { key: 'expenses.export', label: 'Export Expenses' },
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // FILES / ATTACHMENTS
  // ──────────────────────────────────────────────────────────────
  {
    id: 'files',
    label: 'Files',
    description: 'File and attachment management',
    permissions: [
      { key: 'files.view', label: 'View Files' },
      { key: 'files.download', label: 'Download Files' },
      { key: 'files.upload', label: 'Upload Files' },
      { key: 'files.edit', label: 'Rename / Organize Files' },
      { key: 'files.delete', label: 'Delete Files' },
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // AUDIT LOG
  // ──────────────────────────────────────────────────────────────
  {
    id: 'audit_log',
    label: 'Audit Log',
    description: 'Activity history and change logs',
    permissions: [
      { key: 'audit_log.view', label: 'View Audit Log' },
      {
        key: 'audit_log.view_all',
        label: "View All Users' Activity",
        description: 'See audit entries for every user, not just your own',
      },
      { key: 'audit_log.export', label: 'Export Audit Log' },
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // SYSTEM — virtual permissions exposing backend-implicit behavior
  // ──────────────────────────────────────────────────────────────
  {
    id: 'system',
    label: 'System',
    description:
      'Platform-level and implicit permissions. Normally granted to every role — toggle off only for extremely restricted accounts.',
    permissions: [
      {
        key: 'system.self_edit',
        label: 'Edit Own Record',
        description:
          'Everyone needs this to update their own profile fields, notification prefs, etc. Turning this off creates a view-only account.',
      },
      {
        key: 'system.lookup_read',
        label: 'Read Lookup Tables',
        description:
          'Access read-only lookup data the app needs to render basic UI (project types, roles, categories). Turning this off breaks most views.',
      },
      { key: 'system.notifications', label: 'Receive Notifications' },
    ],
  },
];

// ─── Flat list of all permission keys ───

export const ALL_PERMISSIONS: string[] = PERMISSION_CATEGORIES.flatMap((c) =>
  c.permissions.map((p) => p.key)
);

// ─── Permission check helper ───

/**
 * Check whether a permission array grants a specific key.
 *
 * The reserved key `'*'` in a permissions array is a super-admin wildcard —
 * it grants every permission in the system, present and future. Used by the
 * site_admin preset so we never have to chase newly-added permissions.
 */
export function hasPermission(permissions: string[] | undefined, key: string): boolean {
  if (!permissions) return false;
  if (permissions.includes('*')) return true;
  return permissions.includes(key);
}

/**
 * Check whether ANY of several permission keys is granted.
 */
export function hasAnyPermission(permissions: string[] | undefined, keys: string[]): boolean {
  if (!permissions) return false;
  if (permissions.includes('*')) return true;
  return keys.some((k) => permissions.includes(k));
}

// ─── Base permissions every user needs to log in and operate ───
//
// Editing your own profile, reading lookup tables, receiving notifications —
// without these the app is unusable. Every preset inherits them. Admins can
// strip them from custom roles only if they explicitly want an audit-only
// or read-only-kiosk account.

export const BASE_PERMISSIONS: string[] = [
  'system.self_edit',
  'system.lookup_read',
  'system.notifications',
  'team_members.edit_own',
  'organizations.view',
  'projects.view_own',
  'timesheets.view_own',
  'timesheets.create',
  'timesheets.edit_own',
  'timesheets.submit',
  'tasks.view_own',
  'tasks.create',
  'tasks.edit_own',
  'tasks.complete',
  'expenses.view_own',
  'expenses.create',
  'expenses.edit_own',
  'vacation.view_own',
  'vacation.create',
  'vacation.edit',
  'files.view',
  'files.download',
  // Forecasting + dashboards: every user can view their own forecast and
  // save personal views — those are read-only-of-self capabilities.
  'forecasting.view_own',
  'forecasting.edit_own',
  'dashboards.view_own',
  'dashboards.create',
  'dashboards.edit_own',
  'dashboards.delete',
  'dashboards.customize',
];

// ─── Role presets (templates for creating new roles) ───
//
// These are STARTING POINTS. Custom roles are the product direction; admins
// can clone any of these, rename, and trim the permission set for their firm's
// policies. The admin UI surfaces these as a "clone from preset" dropdown
// in the role editor.
//
// site_admin uses the '*' wildcard so future-added permissions automatically
// apply. Everyone else has their explicit set — deliberate choice so admins
// can see exactly what they're granting.

export const ROLE_PRESETS: Record<string, { label: string; permissions: string[] }> = {
  site_admin: {
    label: 'Site Admin (Full Access)',
    permissions: ['*'],
  },

  admin: {
    label: 'Administrator',
    // Everything except hard-delete, rollback, and override actions — soft
    // delete (archive/restore) is fine for admins. Admins can approve
    // reviews, manage team, manage billing, but not force-unlock another
    // user's draft or rollback a published version.
    permissions: ALL_PERMISSIONS.filter(
      (p) =>
        !p.endsWith('.delete') &&
        !p.endsWith('.force_unlock') &&
        !p.endsWith('.version_rollback') &&
        !p.endsWith('.override_draft')
    ),
  },

  publisher: {
    label: 'Publisher',
    // Senior spec/project oversight. Can review, publish, approve, manage
    // the library, but not administer the firm.
    permissions: [
      ...BASE_PERMISSIONS,
      'team_members.view',
      'teams.view',
      'organizations.view',
      'organizations.create',
      'organizations.edit',
      'organizations.edit_contacts',
      'organizations.archive',
      'projects.view_all',
      'projects.view_financials',
      'projects.create',
      'projects.edit',
      'projects.edit_schedule',
      'projects.edit_scope',
      'projects.manage_phases',
      'projects.manage_deliverables',
      'projects.archive',
      'projects.assign_team',
      'projects.assign_anyone',
      'projects.export',
      'specs.view',
      'specs.view_history',
      'specs.create',
      'specs.edit_own',
      'specs.edit_any',
      'specs.submit_for_review',
      'specs.publish',
      'specs.approve_review',
      'specs.reject_review',
      'specs.archive',
      'specs.restore',
      'specs.override_draft',
      'specs.variant_create',
      'specs.variant_edit',
      'specs.variant_delete',
      'specs.comment',
      'specs.resolve_comment',
      'specs.import',
      'specs.export',
      // library_modules.* removed in v2.12.0 — feature retired.
      'smart_blocks.view',
      'smart_blocks.view_history',
      'smart_blocks.create',
      'smart_blocks.edit_own',
      'smart_blocks.edit_any',
      'smart_blocks.submit_for_review',
      'smart_blocks.publish',
      'smart_blocks.approve_review',
      'smart_blocks.reject_review',
      'smart_blocks.archive',
      'smart_blocks.restore',
      'smart_blocks.override_draft',
      'smart_blocks.force_unlock',
      'smart_blocks.version_rollback',
      'smart_blocks.insert',
      'project_specs.view',
      'project_specs.compile',
      'project_specs.edit_own',
      'project_specs.edit_any',
      'project_specs.submit_for_review',
      'project_specs.publish',
      'project_specs.approve_review',
      'project_specs.reject_review',
      'project_specs.create_issuance',
      'project_specs.archive',
      'project_specs.export',
      'formas.view',
      'formas.apply_to_spec',
      'compile.view',
      'compile.run',
      'compile.bulk',
      'compile.export',
      'formatter.view',
      'formatter.run',
      'timesheets.view_team',
      'timesheets.approve',
      'timesheets.reject',
      'vacation.view_team',
      'vacation.approve',
      'reports.view',
      'reports.view_utilization',
      'reports.view_wip',
      'reports.export',
      'forecasting.view_own',
      'forecasting.view_team',
      'forecasting.create',
      'forecasting.edit',
      'tasks.view_team',
      'tasks.view_all',
      'tasks.edit_any',
      'tasks.archive',
      'tasks.assign_team',
      'tasks.assign_anyone',
      'tasks.comment',
      'expenses.view_team',
      'expenses.approve',
      'expenses.reject',
      'files.upload',
      'files.edit',
      'audit_log.view',
    ],
  },

  project_manager: {
    label: 'Project Manager',
    permissions: [
      ...BASE_PERMISSIONS,
      'team_members.view',
      'teams.view',
      'organizations.view',
      'organizations.create',
      'organizations.edit',
      'organizations.edit_contacts',
      'organizations.archive',
      'projects.view_all',
      'projects.view_financials',
      'projects.create',
      'projects.edit',
      'projects.edit_schedule',
      'projects.edit_scope',
      'projects.manage_phases',
      'projects.manage_deliverables',
      'projects.archive',
      'projects.assign_team',
      'projects.assign_anyone',
      'projects.export',
      'specs.view',
      'specs.view_history',
      'specs.create',
      'specs.edit_own',
      'specs.submit_for_review',
      'specs.archive',
      'specs.variant_create',
      'specs.variant_edit',
      'specs.comment',
      'specs.resolve_comment',
      'specs.export',
      // library_modules.* removed in v2.12.0 — feature retired.
      'smart_blocks.view',
      'smart_blocks.view_history',
      'smart_blocks.create',
      'smart_blocks.edit_own',
      'smart_blocks.submit_for_review',
      'smart_blocks.archive',
      'smart_blocks.insert',
      'project_specs.view',
      'project_specs.compile',
      'project_specs.edit_own',
      'project_specs.submit_for_review',
      'project_specs.archive',
      'project_specs.export',
      'formas.view',
      'formas.apply_to_spec',
      'compile.view',
      'compile.run',
      'compile.export',
      'formatter.view',
      'formatter.run',
      'timesheets.view_team',
      'timesheets.approve',
      'vacation.view_team',
      'vacation.approve',
      'invoices.view',
      'reports.view',
      'reports.export',
      'forecasting.view_own',
      'forecasting.view_team',
      'forecasting.create',
      'forecasting.edit',
      'tasks.view_team',
      'tasks.view_all',
      'tasks.edit_any',
      'tasks.archive',
      'tasks.assign_team',
      'tasks.assign_anyone',
      'tasks.comment',
      'expenses.view_team',
      'expenses.approve',
      'files.upload',
      'files.edit',
    ],
  },

  editor: {
    label: 'Editor',
    // Hands-on contributor. Edits everything, submits for review, but doesn't
    // publish or approve.
    permissions: [
      ...BASE_PERMISSIONS,
      'team_members.view',
      'teams.view',
      'organizations.view',
      'projects.view_own',
      'specs.view',
      'specs.view_history',
      'specs.create',
      'specs.edit_own',
      'specs.submit_for_review',
      'specs.variant_create',
      'specs.variant_edit',
      'specs.comment',
      'specs.export',
      // library_modules.* removed in v2.12.0 — feature retired.
      'smart_blocks.view',
      'smart_blocks.create',
      'smart_blocks.edit_own',
      'smart_blocks.submit_for_review',
      'smart_blocks.insert',
      'project_specs.view',
      'project_specs.edit_own',
      'project_specs.submit_for_review',
      'project_specs.export',
      'formas.view',
      'formas.apply_to_spec',
      'compile.view',
      'formatter.view',
      'formatter.run',
      'tasks.edit_own',
      'tasks.comment',
      'files.upload',
    ],
  },

  staff: {
    label: 'Staff Member',
    permissions: [
      ...BASE_PERMISSIONS,
      'team_members.view',
      'specs.view',
      'specs.create',
      'specs.edit_own',
      'specs.submit_for_review',
      'specs.comment',
      'smart_blocks.view',
      'smart_blocks.insert',
      'project_specs.view',
      'formas.view',
      'tasks.edit_own',
      'tasks.comment',
      'files.upload',
    ],
  },

  viewer: {
    label: 'View Only',
    permissions: [
      'system.self_edit',
      'system.lookup_read',
      'system.notifications',
      'team_members.view',
      'team_members.edit_own',
      'organizations.view',
      'projects.view_own',
      'specs.view',
      'specs.view_history',
      'smart_blocks.view',
      'project_specs.view',
      'formas.view',
      'timesheets.view_own',
      'tasks.view_own',
      'expenses.view_own',
      'vacation.view_own',
      'files.view',
      'files.download',
    ],
  },
};

// ─── Legacy boolean → permissions mapping (for data migration) ───
//
// Kept so we can one-shot migrate any leftover role rows in the DB that were
// seeded with the old boolean shape. Not used for live permission checks —
// frontend always goes through hasPermission() against the array.

export const LEGACY_TO_PERMISSIONS: Record<string, string[]> = {
  canAccessAdmin: [
    'admin_center.view',
    'admin_center.manage_roles',
    'admin_center.manage_billing',
    'admin_center.manage_subscription',
    'audit_log.view',
    'timesheets.view_all',
    'timesheets.edit_any',
    'timesheets.approve',
    'vacation.view_all',
    'vacation.approve',
    'expenses.view_all',
    'expenses.edit_any',
    'expenses.approve',
  ],
  canAccessTeam: [
    'team_members.view',
    'team_members.create',
    'team_members.invite',
    'team_members.edit',
    'team_members.deactivate',
    'teams.view',
    'teams.create',
    'teams.edit',
    'teams.manage_members',
  ],
  canAccessInvoices: [
    'invoices.view',
    'invoices.create',
    'invoices.edit',
    'invoices.send',
    'invoices.delete',
    'invoices.export',
  ],
  canAccessAllProjects: [
    'projects.view_all',
    'tasks.view_team',
    'tasks.view_all',
    'tasks.edit_any',
    'tasks.assign_team',
    'tasks.assign_anyone',
    'forecasting.view_own',
    'forecasting.view_all',
    'forecasting.create',
    'forecasting.edit',
  ],
  canAccessBilling: ['reports.view', 'reports.export'],
  canManageSpecs: [
    'specs.view',
    'specs.create',
    'specs.edit_own',
    'specs.edit_any',
    'specs.delete',
    'specs.publish',
    'specs.archive',
    'files.upload',
    'files.delete',
  ],
  canEditProjects: [
    'projects.create',
    'projects.edit',
    'projects.assign_team',
    'projects.assign_anyone',
    'organizations.create',
    'organizations.edit',
  ],
  canDeleteProjects: ['projects.delete'],
  canDeleteClients: ['organizations.delete'],
};

// ─── Derive legacy booleans from permissions (for backward compat) ───
//
// @deprecated — any code still reading these should migrate to direct
// hasPermission() calls against specific keys. Kept only until every
// caller is converted. See the permissions audit for the migration plan.

export interface LegacyPermFlags {
  canAccessAdmin: boolean;
  canAccessTeam: boolean;
  canAccessInvoices: boolean;
  canAccessAllProjects: boolean;
  canAccessBilling: boolean;
  canManageSpecs: boolean;
  canEditProjects: boolean;
  canDeleteProjects: boolean;
  canDeleteClients: boolean;
}

export function permissionsToLegacy(perms: string[]): LegacyPermFlags {
  const has = (k: string) => hasPermission(perms, k);
  return {
    canAccessAdmin:
      has('admin_center.view') ||
      has('admin_center.manage_roles') ||
      has('admin_center.manage_billing') ||
      has('admin_center.manage_subscription'),
    canAccessTeam: has('team_members.view'),
    canAccessInvoices: has('invoices.view'),
    canAccessAllProjects: has('projects.view_all'),
    canAccessBilling: has('reports.view'),
    canManageSpecs: has('specs.view'),
    canEditProjects: has('projects.edit'),
    canDeleteProjects: has('projects.delete'),
    canDeleteClients: has('organizations.delete'),
  };
}

/** One-shot migration helper: convert old `canXxx: true` role rows into a
 *  permissions array. Only used by data-fix scripts. */
export function legacyToPermissions(flags: Record<string, boolean>): string[] {
  const perms = new Set<string>(BASE_PERMISSIONS);
  for (const [flag, keys] of Object.entries(LEGACY_TO_PERMISSIONS)) {
    if (flags[flag]) {
      keys.forEach((k) => perms.add(k));
    }
  }
  return [...perms].sort();
}
